import { faArrowUpRightFromSquare, faChevronDown, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useGameSessionHistory } from '../api/useGameSessionHistory'
import { GameSessionList } from './GameSessionList'

/**
 * What an account has been doing, for somebody deciding whether to pay it.
 *
 * Three questions, and they are three screens rather than one long one:
 * what has it played lately, is any of that out of character, and who has it
 * been playing against. Stacked, the answer to the third was two hundred rows
 * below the question; switched, each is the whole of what is in front of you.
 *
 * Every tab renders the same session rows the rest of Team Tools uses -- the
 * server, the IP, the duration, the fee, the kills, the earnings, how it
 * ended. A reviewer who has learned to read that table should not have to
 * learn a second one here.
 *
 * "Their last 30 days" is measured from the account, not from today. An
 * account that stopped in March has nothing in the calendar's last 30 days,
 * and a list that comes back empty for it says something false: it played
 * plenty, then stopped.
 */

type Opponent = { username: string; user_id: number; count: number }

type Cheat = {
    flagged_sessions: number
    total_matches: number
    reports: number
    actioned: number
    dismissed: number
    open: number
}

type Play = {
    cheat: Cheat | null
    window: { from: string; to: string; days: number; typical_kills: number; unusual_at: number } | null
    total: number
    sessions: Array<{ id: number; unusual: boolean; why: string }>
    killed: Opponent[]
    killed_by: Opponent[]
}

const RECENT_DAYS = 30

const day = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—'

type Tab = 'recent' | 'unusual' | 'flagged' | 'opponents'

export const PlayPanel = ({ userId }: { userId: number }) => {
    const [tab, setTab] = useState<Tab>('recent')
    const [play, setPlay] = useState<Play | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let alive = true
        fetch(`/team-tools/api/player/${userId}/play`)
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((data) => alive && setPlay(data))
            .catch(() => alive && setFailed(true))
        return () => {
            alive = false
        }
    }, [userId])

    const unusualCount = play?.sessions.filter((s) => s.unusual).length ?? 0

    const TABS: Array<{ key: Tab; label: string; count?: number }> = [
        { key: 'recent', label: `Last ${RECENT_DAYS} days`, count: play?.total },
        { key: 'unusual', label: 'Unusual for the game', count: unusualCount },
        { key: 'flagged', label: 'Flagged by anti-cheat', count: play?.cheat?.flagged_sessions },
        { key: 'opponents', label: 'Who they played' },
    ]

    return (
        <div className='flex flex-col gap-3 pt-2'>
            <div className='flex flex-row flex-wrap items-center gap-2'>
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type='button'
                        onClick={() => setTab(t.key)}
                        className={twMerge(
                            'rounded border px-3 py-1 text-[10px] font-bold uppercase tracking-widest',
                            tab === t.key
                                ? 'border-white/60 bg-white/10 text-white'
                                : 'border-white/15 text-white/50 hover:border-white/40 hover:text-white'
                        )}
                    >
                        {t.label}
                        {t.count !== undefined && (
                            <span className='ml-2 font-normal opacity-70'>{t.count}</span>
                        )}
                    </button>
                ))}

                {play?.window && (
                    <span className='text-white/35'>
                        {day(play.window.from)} – {day(play.window.to)}
                    </span>
                )}
            </div>

            {failed && <span className='text-white/40'>Their play could not be loaded.</span>}
            {!failed && !play && (
                <span className='flex flex-row items-center gap-2 text-white/40'>
                    <FontAwesomeIcon icon={faSpinner} spin /> Reading their sessions…
                </span>
            )}
            {play && !play.window && (
                <span className='text-white/40'>This account has never played a match.</span>
            )}

            {play?.window && tab === 'recent' && <RecentSessions userId={userId} />}

            {play?.window && tab === 'unusual' && (
                <UnusualSessions userId={userId} play={play} />
            )}

            {play?.window && tab === 'flagged' && <Flagged userId={userId} cheat={play.cheat} />}

            {play?.window && tab === 'opponents' && (
                <div className='grid gap-4 md:grid-cols-2'>
                    <Opponents
                        title='They killed most'
                        rows={play.killed}
                        userId={userId}
                        killedThem={false}
                    />
                    <Opponents
                        title='They died to most'
                        rows={play.killed_by}
                        userId={userId}
                        killedThem={true}
                    />
                </div>
            )}
        </div>
    )
}

/**
 * Their last 30 days, with a box for narrowing it to one opponent.
 *
 * "Did they play <name>" is the question a reviewer arrives with after
 * reading a shared-machine finding, and answering it meant leaving for the
 * opponents tab and hoping the name was in the top ten. The name is resolved
 * on the server, so a username seen anywhere else can be pasted straight in.
 */
const RecentSessions = ({ userId }: { userId: number }) => {
    const [typed, setTyped] = useState('')
    const [against, setAgainst] = useState('')

    return (
        <div className='flex flex-col gap-2'>
            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    setAgainst(typed.trim())
                }}
                className='flex flex-row flex-wrap items-center gap-2'
            >
                <input
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder='Only matches against a player…'
                    className='w-64 rounded border border-white/20 bg-transparent px-2 py-0.5 text-xs outline-none focus:border-white/40'
                />
                <button
                    type='submit'
                    className='rounded border border-white/20 px-3 py-0.5 text-[10px] uppercase tracking-widest text-white/50 hover:border-white/40 hover:text-white'
                >
                    Search
                </button>
                {against && (
                    <button
                        type='button'
                        onClick={() => {
                            setTyped('')
                            setAgainst('')
                        }}
                        className='text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
                    >
                        show all again
                    </button>
                )}
                {against && <span className='text-white/50'>matches with {against}</span>}
            </form>
            <Sessions userId={userId} recentDays={RECENT_DAYS} opponentUsername={against} />
        </div>
    )
}

/** The ordinary session table, filtered. */
const Sessions = ({
    userId,
    recentDays = 0,
    opponentId = 0,
    opponentUsername = '',
    opponentKilledThem = false,
}: {
    userId: number
    recentDays?: number
    opponentId?: number
    opponentUsername?: string
    opponentKilledThem?: boolean
}) => {
    const { sessions, count, fetchNextPage, hasNextPage, isLoading } = useGameSessionHistory({
        user_id: userId,
        recentDays,
        opponentId,
        opponentUsername,
        opponentKilledThem,
    })

    if (isLoading)
        return (
            <span className='flex flex-row items-center gap-2 text-white/40'>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading…
            </span>
        )
    if (!sessions?.length) return <span className='text-white/40'>No matches here.</span>

    return (
        <div className='flex max-w-full flex-col gap-2 overflow-x-auto'>
            <GameSessionList sessions={sessions} />
            {hasNextPage && (
                <button
                    type='button'
                    onClick={() => fetchNextPage()}
                    className='self-center rounded border border-white/20 px-3 py-1 text-[10px] uppercase tracking-widest text-white/50 hover:border-white/40 hover:text-white'
                >
                    Show more of {count}
                </button>
            )}
        </div>
    )
}

/**
 * The sessions that are out of character for this account.
 *
 * Measured against the account's own median rather than the population: a
 * player who takes fifteen kills every match is not unusual on the match
 * where they take fifteen. That is why a genuinely good player does not light
 * this up the way a fixed threshold would make them.
 *
 * The kill floor comes from the same reading, so the ordinary session table
 * can do the filtering and the rows stay the rows everybody knows.
 */
const UnusualSessions = ({ userId, play }: { userId: number; play: Play }) => {
    const floor = play.window?.unusual_at ?? 0
    const { sessions, isLoading } = useGameSessionHistory({
        user_id: userId,
        recentDays: RECENT_DAYS,
        killsMin: String(floor),
    })

    if (isLoading)
        return (
            <span className='flex flex-row items-center gap-2 text-white/40'>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading…
            </span>
        )
    if (!sessions?.length)
        return (
            <span className='text-white/40'>
                Nothing unusual. An ordinary player takes about {play.window?.typical_kills} kills a match,
                and nothing in this window reaches {floor}.
            </span>
        )

    return (
        <div className='flex max-w-full flex-col gap-2 overflow-x-auto'>
            <span className='text-amber-300'>
                {sessions.length} match{sessions.length === 1 ? '' : 'es'} at {floor} kills or more. An
                ordinary player takes about {play.window?.typical_kills} a match.
            </span>
            <GameSessionList sessions={sessions} />
        </div>
    )
}

/**
 * What the anti-cheat has on this account, and the matches behind it.
 *
 * Counted over the whole career rather than the 30-day window: a flag is a
 * fact about an account, not about a fortnight, and an account flagged four
 * times last spring and clean since is a different thing from one that has
 * never been flagged. A windowed count reports both as zero.
 *
 * Flagged and CONFIRMED are kept apart, in that order of weight. A flag is
 * the detector or an admin kick; a confirmed report is a person having
 * concluded it was real; a dismissed one is a person having concluded it was
 * not, and it lowers the count rather than sitting beside it. Reading them as
 * one number is how an account with three dismissals looks like an account
 * with three findings.
 */
const Flagged = ({ userId, cheat }: { userId: number; cheat: Cheat | null }) => {
    const { sessions, isLoading } = useGameSessionHistory({
        user_id: userId,
        cheatedOnly: true,
    })

    if (!cheat || cheat.flagged_sessions === 0) {
        return (
            <span className='text-white/40'>
                The anti-cheat has never flagged a match on this account
                {cheat && cheat.dismissed > 0
                    ? `, and ${cheat.dismissed} report${cheat.dismissed === 1 ? ' was' : 's were'} looked at and dismissed.`
                    : '.'}
            </span>
        )
    }

    const share = cheat.total_matches
        ? Math.round((cheat.flagged_sessions / cheat.total_matches) * 100)
        : 0

    return (
        <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-2 rounded-md border border-amber-400/40 bg-amber-400/[0.06] px-3 py-2'>
                <div className='flex flex-row flex-wrap items-baseline gap-x-6 gap-y-2'>
                    <span className='flex flex-col'>
                        <span className='text-2xl font-bold text-amber-300'>
                            {cheat.flagged_sessions}
                        </span>
                        <span className='text-[10px] uppercase tracking-widest text-white/50'>
                            matches flagged
                        </span>
                    </span>
                    <span className='flex flex-col'>
                        <span className='text-2xl font-bold text-white'>{share}%</span>
                        <span className='text-[10px] uppercase tracking-widest text-white/50'>
                            of their {cheat.total_matches} matches
                        </span>
                    </span>
                    <span className='flex flex-col'>
                        <span
                            className={twMerge(
                                'text-2xl font-bold',
                                cheat.actioned > 0 ? 'text-red-400' : 'text-white/50'
                            )}
                        >
                            {cheat.actioned}
                        </span>
                        <span className='text-[10px] uppercase tracking-widest text-white/50'>
                            confirmed by a person
                        </span>
                    </span>
                    {cheat.open > 0 && (
                        <span className='flex flex-col'>
                            <span className='text-2xl font-bold text-amber-300'>{cheat.open}</span>
                            <span className='text-[10px] uppercase tracking-widest text-white/50'>
                                still unread
                            </span>
                        </span>
                    )}
                    {cheat.dismissed > 0 && (
                        <span className='flex flex-col'>
                            <span className='text-2xl font-bold text-white/50'>{cheat.dismissed}</span>
                            <span className='text-[10px] uppercase tracking-widest text-white/50'>
                                looked at, dismissed
                            </span>
                        </span>
                    )}
                </div>
                <span className='font-normal text-white/50'>
                    {cheat.actioned > 0
                        ? 'Somebody has read these and concluded at least one was real.'
                        : cheat.open > 0
                          ? 'Nobody has concluded either way yet. A flag is a reason to look, not a finding.'
                          : 'Every report on this account has been read and dismissed. The flags below are what the detector saw.'}
                </span>
            </div>

            {isLoading ? (
                <span className='flex flex-row items-center gap-2 text-white/40'>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading…
                </span>
            ) : (
                <div className='flex max-w-full flex-col gap-2 overflow-x-auto'>
                    <GameSessionList sessions={sessions ?? []} />
                </div>
            )}
        </div>
    )
}

/** Who they played, and the matches behind each count. */
const Opponents = ({
    title,
    rows,
    userId,
    killedThem,
}: {
    title: string
    rows: Opponent[]
    userId: number
    /** True for "they died to", false for "they killed". */
    killedThem: boolean
}) => {
    const [open, setOpen] = useState<number | null>(null)

    return (
        <div className='flex flex-col gap-1'>
            <span className='text-[10px] font-bold uppercase tracking-widest text-white/60'>{title}</span>
            {rows.length === 0 ? (
                <span className='font-normal text-white/35'>Nobody.</span>
            ) : (
                rows.map((row) => (
                    <div key={row.username} className='flex flex-col border-b border-white/5'>
                        <div className='flex flex-row items-center gap-2 py-1'>
                            <button
                                type='button'
                                onClick={() => setOpen(open === row.user_id ? null : row.user_id)}
                                className='flex min-w-0 flex-1 flex-row items-center gap-2 text-left hover:text-white'
                                title='Show the matches these two met in'
                            >
                                <FontAwesomeIcon
                                    icon={open === row.user_id ? faChevronDown : faChevronRight}
                                    className='text-white/30'
                                />
                                <span className='truncate'>{row.username}</span>
                            </button>
                            <a
                                href={`/team-tools/accounts/users/${row.username}`}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={(e) => e.stopPropagation()}
                                title={`Open ${row.username} in a new tab`}
                                className='rounded border border-white/15 px-1.5 text-[10px] text-white/40 hover:border-white/40 hover:text-white'
                            >
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            </a>
                            <span className='tabular-nums'>{row.count}</span>
                        </div>
                        {open === row.user_id && (
                            <div className='pb-2'>
                                <Sessions
                                    userId={userId}
                                    opponentId={row.user_id}
                                    opponentKilledThem={killedThem}
                                />
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    )
}
